"use client"

import React, { useState, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import Breadcrumb from "../../utils/breadcrumbs"
import { FiEye, FiTrash2 } from "react-icons/fi"
import ViewPaymentOffcanvas from "../../components/payments/view-payment-offcanvas"
import Loader from "../../utils/loader"
import ConfirmationModal from "../../ui/confirmation-modal"
import toast from "react-hot-toast"

export interface IPayment {
    id: number
    user_id: number
    amount: number
    currency: string
    payment_method: string
    status: string
    transaction_id: string
    description?: string
    created_at?: Date
    updated_at?: Date
    user?: {
        first_name: string
        last_name: string
        email: string
    }
}

const Payments: React.FC = () => {
    const navigate = useNavigate()
    const [selectedPayment, setSelectedPayment] = useState<IPayment | null>(null)
    const [searchParams, setSearchParams] = useState<Record<string, string>>({})
    const [showViewPaymentCanvas, setShowViewPaymentCanvas] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [paymentToDelete, setPaymentToDelete] = useState<IPayment | null>(null)
    const [payments, setPayments] = useState<IPayment[]>([])
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [loading, setLoading] = useState(false)
    const [limit, setLimit] = useState(10)

    // Dummy payments data
    const dummyPayments: IPayment[] = [
        {
            id: 1,
            user_id: 1,
            amount: 99.99,
            currency: "USD",
            payment_method: "Credit Card",
            status: "completed",
            transaction_id: "TXN_001_2024",
            description: "Monthly subscription payment",
            created_at: new Date("2024-01-15T10:30:00Z"),
            user: {
                first_name: "John",
                last_name: "Doe",
                email: "john.doe@example.com",
            },
        },
        {
            id: 2,
            user_id: 2,
            amount: 49.99,
            currency: "USD",
            payment_method: "PayPal",
            status: "pending",
            transaction_id: "TXN_002_2024",
            description: "One-time purchase",
            created_at: new Date("2024-02-20T14:45:00Z"),
            user: {
                first_name: "Jane",
                last_name: "Smith",
                email: "jane.smith@example.com",
            },
        },
        {
            id: 3,
            user_id: 3,
            amount: 199.99,
            currency: "USD",
            payment_method: "Bank Transfer",
            status: "failed",
            transaction_id: "TXN_003_2024",
            description: "Annual subscription payment",
            created_at: new Date("2024-03-10T09:15:00Z"),
            user: {
                first_name: "Michael",
                last_name: "Johnson",
                email: "michael.johnson@example.com",
            },
        },
    ]

    // Filter payments based on search parameters
    const filteredPayments = useMemo(() => {
        return payments.filter((payment) => {
            const userMatch =
                !searchParams.user ||
                `${payment.user?.first_name} ${payment.user?.last_name}`.toLowerCase().includes(searchParams.user.toLowerCase())

            const transactionMatch =
                !searchParams.transaction_id ||
                payment.transaction_id.toLowerCase().includes(searchParams.transaction_id.toLowerCase())

            const statusMatch = !searchParams.status || payment.status.toLowerCase() === searchParams.status.toLowerCase()

            return userMatch && transactionMatch && statusMatch
        })
    }, [payments, searchParams])

    // Fetch payments - using dummy data
    const fetchPayments = () => {
        setLoading(true)
        setTimeout(() => {
            setPayments(dummyPayments)
            setTotalPages(1)
            setLoading(false)
        }, 500)
    }

    // Fetch payments
    React.useEffect(() => {
        fetchPayments()
    }, [currentPage, limit])

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = { ...prev }
            if (value) {
                newParams[column] = value
            } else {
                delete newParams[column]
            }
            return newParams
        })
        setCurrentPage(1)
    }

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case "completed":
                return "bg-green-100 text-green-800"
            case "pending":
                return "bg-yellow-100 text-yellow-800"
            case "failed":
                return "bg-red-100 text-red-800"
            case "refunded":
                return "bg-blue-100 text-blue-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    // Handle view payment
    const handleViewPayment = (payment: IPayment) => {
        setSelectedPayment(payment)
        setShowViewPaymentCanvas(true)
    }

    // Handle delete
    const handleDeleteClick = (payment: IPayment) => {
        setPaymentToDelete(payment)
        setDeleteConfirmOpen(true)
    }

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (paymentToDelete) {
            try {
                // Simulate delete action
                setPayments(payments.filter((payment) => payment.id !== paymentToDelete.id))
                toast.success(`Payment ${paymentToDelete.transaction_id} deleted successfully!`)
            } catch (err) {
                toast.error("Error deleting payment")
            }
            setDeleteConfirmOpen(false)
            setPaymentToDelete(null)
        }
    }

    return (
        <div className="w-full min-h-screen bg-white p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Payments
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    User
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Transaction ID
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Amount
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Payment Method
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search User"
                                        value={searchParams.user || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("user", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Transaction ID"
                                        value={searchParams.transaction_id || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("transaction_id", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                                <td className="px-6 py-2 border-r"></td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("status", e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="completed">Completed</option>
                                        <option value="pending">Pending</option>
                                        <option value="failed">Failed</option>
                                        <option value="refunded">Refunded</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="text-center">
                                        <Loader />
                                    </td>
                                </tr>
                            ) : filteredPayments.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-8 text-gray-500">
                                        No payments found
                                    </td>
                                </tr>
                            ) : (
                                filteredPayments.map((payment) => (
                                    <tr
                                        key={payment.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleViewPayment(payment)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex font-semibold items-center">
                                                {payment.user?.first_name} {payment.user?.last_name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">{payment.transaction_id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className="font-semibold">
                                                {payment.currency} {payment.amount.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">{payment.payment_method}</td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(payment.status)}`}>
                                                {payment.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => handleViewPayment(payment)}
                                                    className="text-primary-600 hover:text-primary-700"
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteClick(payment)} 
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    {/* <Pagination
                        limit={limit}
                        setLimit={setLimit}
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    /> */}
                </div>

                <ViewPaymentOffcanvas
                    showViewPaymentCanvas={showViewPaymentCanvas}
                    setShowViewPaymentCanvas={setShowViewPaymentCanvas}
                    payment={selectedPayment}
                />

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Payment"
                        description="Are you sure you want to delete this payment? This action cannot be undone."
                        loading={loading}
                    />
                )}
            </div>
        </div>
    )
}

export default Payments